<template>
  <div>
    <div class="detail-header__breadcrumb">
      <span @click="goToStyles">
        <i class="el-icon-back" />
        Back to style overview
      </span>
    </div>
    <div class="detail-header__title">
      <StitchTooltip :message="item.name">
        <h1>
          {{ truncateText(item.name, 40) }}
        </h1>
      </StitchTooltip>
      <StitchTooltip
        v-if="libraryType === LIBRARY_TYPE.STYLE"
        :message="`QC - ${qualityCheckTooltip}`"
        placement="right"
      >
        <span class="detail-header__title-quality-check">
          <i
            v-if="styleQualityCheckStatus === null"
            class="el-icon-remove-outline not-set"
          />
          <i
            v-else-if="styleQualityCheckStatus"
            class="el-icon-success approved"
          />
          <i
            v-else
            class="el-icon-error rejected"
          />
        </span>
      </StitchTooltip>
      <StitchTooltip
        v-if="libraryType === LIBRARY_TYPE.STYLE"
        :message="watchState ? `Remove from Watchlist` : `Add to Watchlist`"
        placement="right"
      >
        <span
          class="detail-header__title-watchlist"
          @click="toggleWatchState"
        >
          <StitchLoader
            v-if="isTogglingWatch"
            size="mini"
            class="detail-header__title-watchlist-loading"
          />
          <span v-else>
            <i
              v-if="!watchState"
              class="el-icon-star-off not-set"
            />
            <i
              v-else
              class="el-icon-star-on watched"
            />
          </span>
        </span>
      </StitchTooltip>
      <div class="detail-header__actions">
        <StitchTooltip message="Actions">
          <StitchDropdown
            v-if="!isVendorUser"
            :dropdown-items="criticalStatusTransitions"
            :append-to-body="false"
            :title="`Style actions`"
            class="detail-header__dropdown"
            :icon-type="ICON_TYPE.MENU"
          />
        </StitchTooltip>

        <!-- Style Status Action form -->
        <FormGenerator
          :show-form-generator="showStyleStatusActionForm"
          :form-configuration="styleStatusActionFormConfiguration"
          :additional-data="item"
          :original-data="actionConfirmationFormData"
          :default-data="styleStatusActionDefaultFormData"
          :options="styleStatusActionReasons"
          :is-creation="true"
          @form-close="onStyleStatusActionFormClose"
          @form-complete="completeStyleStatusActionForm"
        />
      </div>
    </div>
    <div
      v-if="itemMeta.length > 0"
      class="detail-header__meta"
    >
      <ElementTag
        v-for="(value, index) in itemMeta"
        :key="index"
        size="mini"
        type="info"
      >
        {{ value }}
      </ElementTag>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DataUtils } from '@/mixins/utils.js'
import { formatToLocaleDateShort, truncateText } from '@/services/utils'
import { getFormConfig } from '@/services/formUtils'
import StitchTooltip from '@/components/common/StitchTooltip.vue'
import { LIBRARY_TYPE } from '@/constants/libraryType'
import { ItemShape } from '@/types'
import { ICON_TYPE, STYLE_TYPE } from '@/constants/dropdownOptions'
import {
  ASSET_STATUS,
  REASONS_FOR_CANCELLATION,
  REASONS_FOR_RESUBMITTING,
  CRITICAL_STATUSES,
  CRITICAL_STATUSES_NO_VALIDATION
} from '@/constants/assetStatuses'
import { FORM_NAME } from '@/constants/formName'
import { USER_ROLE } from '@/constants/roleType'

export default {
  name: 'DetailHeader',

  components: {
    StitchTooltip
  },

  mixins: [DataUtils],

  props: {
    item: ItemShape.loose.isRequired
  },

  emits: ['edit-item', 'delete-item', 'asset-status-action'],

  data () {
    return {
      LIBRARY_TYPE,
      ICON_TYPE,
      ASSET_STATUS,
      USER_ROLE,
      watchState: false,
      isTogglingWatch: false,
      isLoadingStatus: false,
      showStyleStatusActionForm: false,
      styleStatusActionDefaultFormData: null,
      styleStatusActionReasons: {},
      styleQualityCheckStatus: null,
      disabledMessage:
        'As a vendor user, you are not allowed to perform this action.'
    }
  },

  computed: {
    ...mapGetters([
      'getItemStatus',
      'getStyleQualityStatus',
      'getStyleResponsibleProduct'
    ]),

    ...mapGetters({
      user: 'getCognitoUserData'
    }),

    /**
     * @returns {object}
     */
    itemDetail () {
      return this.getItemStatus()
    },

    /**
     * @returns {object}
     */
    qualityCheck () {
      if (this.item !== null && this.libraryType === LIBRARY_TYPE.STYLE) {
        return this.getStyleQualityStatus({ styleId: this.item.id })
      }

      return null
    },

    /**
     * @returns {string}
     */
    qualityCheckTooltip () {
      return this.styleQualityCheckStatus === null
        ? 'Not Set'
        : this.styleQualityCheckStatus
          ? 'Passed'
          : 'Needs Improvements'
    },

    /**
     * @returns {boolean}
     */
    isVendorUser () {
      return this.user.role === USER_ROLE.VENDOR.name
    },

    /**
     * @returns {Array}
     */
    criticalStatusTransitions () {
      if (!this.itemDetail) {
        return []
      }

      return this.itemDetail.transitions
        .filter(transition => CRITICAL_STATUSES.includes(transition.value))
        .map(transition => {
          return {
            name: transition.action,
            disabled: !transition.is_possible,
            method: () =>
              CRITICAL_STATUSES_NO_VALIDATION.includes(transition.value)
                ? this.updateStatusWithoutConfirmation(transition.value)
                : this.openAdvancedTransitionAction(transition),
            // Make sure to not display a message when status is already selected
            ...(transition.label !== this.item.assetStatus
              ? {
                  disabledMessage: [
                    `You cannot change status from ${this.item.assetStatus} to ${transition.label}.`
                  ]
                }
              : {})
          }
        })
    },

    /**
     * @returns {Array}
     */
    dropdownItems () {
      return [
        {
          name: 'Edit Information',
          method: () => this.emitEditItem()
        },
        {
          name: `Delete ${this.libraryType}`,
          styles: [STYLE_TYPE.DANGER],
          method: () => this.emitDeleteItem()
        }
      ]
    },

    /**
     * @returns {object}
     */
    itemMeta () {
      return this.item.meta
    },

    /**
     * @returns {string}
     */
    libraryType () {
      return this.item.libraryType
    },

    /**
     * @returns {object}
     */
    styleStatusActionFormConfiguration () {
      return getFormConfig(FORM_NAME.STATUS_CHANGE_CONFIRMATION)
    },

    /**
     * @returns {object}
     */
    actionConfirmationFormData () {
      return {
        name: this.item.name,
        plmCode: this.item.plmCode
      }
    }
  },

  /**
   * Vue watcher that syncs the `watchState` data property
   * with the `item.watched` property.
   *
   * This ensures that when the parent-provided `item.watched` changes,
   * the internal `watchState` stays up to date.
   *
   * @param {boolean} val - The new value of `item.watched`
   */
  watch: {
    'item.watched': {
      immediate: true,
      /**
       * Handler for changes to item.watched
       *
       * @param {boolean} val - New watched state from item
       */
      handler (val) {
        this.watchState = val
      }
    },

    'item.qualityCheck': {
      immediate: true,
      /**
       * Handler for changes to item.qualityCheck
       *
       * @param {boolean} val - New qualityCheck state from item
       */
      handler (val) {
        this.styleQualityCheckStatus = val
      }
    }
  },

  created () {
    if (this.item !== null && this.libraryType === LIBRARY_TYPE.STYLE) {
      this.styleQualityCheckStatus = this.qualityCheck

      if (this.item && typeof this.item.watched === 'boolean') {
        this.watchState = this.item.watched
      }
    }
  },

  beforeDestroy () {
    this.clearItemStatus({ libraryType: this.libraryType })
  },

  methods: {
    truncateText,

    formatToLocaleDateShort,

    ...mapActions(['updateAssetStatus', 'toggleWatchStyle', 'clearItemStatus']),

    /**
     */
    goToStyles () {
      this.$router.push({
        path: '/styles/',
        query: {
          brand: this.$route.query.brand,
          group_id: this.$route.query.group_id
        }
      })
    },

    /**
     * Toggles the "watched" state of the current style.
     *
     * @returns {Promise<void>}
     */
    async toggleWatchState () {
      this.isTogglingWatch = true
      const messageDuration = 5000

      try {
        const updated = await this.toggleWatchStyle({
          styleId: this.item.id,
          watched: !this.watchState
        })

        this.watchState = updated

        if (this.watchState) {
          this.$message({
            message: 'You have added this style to your watch list.',
            type: 'info',
            showClose: true,
            duration: messageDuration
          })
        } else {
          this.$message({
            message: 'You have removed this style from your watch list.',
            type: 'warning',
            showClose: true,
            duration: messageDuration
          })
        }
      } catch (error) {
        this.$message({
          message: 'Failed to update watch state.',
          type: 'error',
          showClose: true,
          duration: messageDuration
        })
      } finally {
        this.isTogglingWatch = false
      }
    },

    /**
     * @param {string} status
     */
    async updateStatusWithoutConfirmation (status) {
      this.isLoadingStatus = true

      await this.updateAssetStatus({
        id: this.item.id,
        assetStatus: status,
        isCritical: false
      })

      this.isLoadingStatus = false
    },

    /**
     * @param {object} transition
     */
    openAdvancedTransitionAction (transition) {
      this.styleStatusActionDefaultFormData = {
        assetStatus: transition.label
      }

      this.styleStatusActionReasons = { changeReasons: [] }

      if (transition.label === ASSET_STATUS.CANCELLED.label) {
        this.styleStatusActionReasons.changeReasons = REASONS_FOR_CANCELLATION
      } else if (transition.label === ASSET_STATUS.RESUBMIT.label) {
        this.styleStatusActionReasons.changeReasons = REASONS_FOR_RESUBMITTING
      }

      this.showStyleStatusActionForm = true
    },

    /**
     */
    emitEditItem () {
      this.$emit('edit-item', { styleId: this.item.id })
    },

    /**
     * @returns {Promise<MessageBoxData>}
     */
    emitDeleteItem () {
      return this.$confirm(
        `This will permanently delete ${this.item.name}. Continue?`,
        'Warning',
        {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning',
          showClose: false
        }
      )
        .then(() => {
          this.$emit('delete-item', {
            id: this.item.id,
            name: this.item.name
          })
        })
        .catch(() => {})
    },

    /**
     *
     */
    onStyleStatusActionFormClose () {
      this.showStyleStatusActionForm = false
    },

    /**
     * @param {number} success
     */
    completeStyleStatusActionForm (success) {
      if (success) {
        this.onStyleStatusActionFormClose()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.detail-header__title {
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: spacing(1);
  padding-left: spacing(2);
}

.detail-header__breadcrumb {
  margin-top: spacing(2.5);
  padding: 0 spacing(2);
  color: $grey-light;
  font-size: $font-size-label;
  cursor: pointer;
}

.detail-header__title-quality-check {
  margin: 0 spacing(1.5);
  font-size: spacing(3.5);

  .approved {
    color: $status-approved;
  }

  .rejected {
    color: $red;
  }

  .not-set {
    color: $grey-light;
  }
}

.detail-header__title-watchlist {
  font-size: spacing(3.5);
  transition: transform 0.2s ease;

  .watched {
    color: $blue;
  }

  .not-set {
    color: $grey-light;
  }
}

.detail-header__title-watchlist:hover {
  transform: scale(1.2);
  cursor: pointer;
}

.detail-header__title-watchlist-loading {
  align-items: start;
  padding-top: spacing(1.5);
}

.detail-header__button-actions {
  font-size: spacing(1.8);
  cursor: pointer;
  transition: $transition;
}

.detail-header__button-actions:hover {
  font-size: spacing(2);
}

.detail-header__meta {
  margin: spacing(2) 0 0 0;
  padding: 0 spacing(2);

  > * {
    margin: 0 spacing(1/2) spacing(2);
  }
}

.detail-header__dropdown {
  display: inline-flex;

  /deep/ .el-button {
    padding: 0;
  }
}

.detail-header__actions {
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  margin-top: spacing(1);
  margin-right: spacing(2);

  > * {
    margin-right: spacing(2);
  }
}

.detail-header__edit-info {
  display: flex;
  align-items: center;
  margin-right: spacing(0);

  > * {
    margin-right: spacing(2);
  }
}
</style>

<style lang="scss">
.detail-header__qc-neutral-state .el-switch__core {
  background-color: $grey-very-light !important;
  border-color: $grey-very-light !important;
}
</style>
